import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';

type CountdownSlimProps = {
    logo: React.FC<React.SVGProps<SVGElement>>;
    tagline: string;
    eventStartDate: Date;
    eventEndDate: Date;
};

const CountdownSwiperSlide: React.FC<CountdownSlimProps> = ({ logo: Logo, tagline, eventStartDate, eventEndDate }) => {
    // Used to determine on initial render if we should show the tagline or "see you next year"
    const now = new Date();
    const isPastEventStartDate = (eventStartDate.getTime() - now.getTime()) <= 0;
    const dayAfterEndDate = DateTime.fromJSDate(eventEndDate).plus({day: 1});
    const isPastEventEndDate = now.getTime() - dayAfterEndDate.toMillis() > 0;
    const isEventOver = isPastEventStartDate && isPastEventEndDate;

    // updated to allow null value
    const [timeLeft, setTimeLeft] = useState<{ days: number, hours: number, minutes: number, seconds: number } | null>(null);

    const [currentTagline, setCurrentTagline] = useState(isEventOver ? 'See you next year!' : tagline);

    // If we already know it's past the event date during initial render, we can safely assume we don't need to calculate the time left
    const [calculatingTimeLeft, setCalculatingTimeLeft] = useState(isPastEventStartDate ? false : true);

    const countdownTick = (timer?: NodeJS.Timeout) => {
        const newTimeLeft = getTimeLeftUntilStart(eventStartDate);

        if (calculatingTimeLeft) {
            setCalculatingTimeLeft(false);
        }
    
        if (newTimeLeft === null && !isPastEventEndDate) {
            // We're past the start date, but not the end date (keep tagline but hide the countdown)
            setTimeLeft(null);
        } else if (newTimeLeft === null) {
            // We're past the end date (update tagline, hide the countdown, and clear the interval)
            setTimeLeft(null);
            setCurrentTagline('See you next year!');
            clearInterval(timer);
        } else {
            // Still counting down to the start of the event
            setTimeLeft(newTimeLeft);
        }

        return newTimeLeft !== null || newTimeLeft === null && !isPastEventEndDate;
    };

    const getCountdownMarkup = () => {
        // During the initial server render, we don't want to show the time left yet, so the countdown should render with no values
        if (calculatingTimeLeft) {
            return (
                <>
                    <div><span className="count">&nbsp;</span>DAYS</div>
                    <div><span className="count">&nbsp;</span>HOURS</div>
                    <div><span className="count">&nbsp;</span>MIN</div>
                    <div><span className="count">&nbsp;</span>SEC</div>
                </>
            );
        }

        // If this isn't the initial render and we have a timeLeft object, render the countdown
        if (timeLeft !== null) {
            return (
                <>
                    <div><span className="count">{timeLeft.days}</span>DAYS</div>
                    <div><span className="count">{timeLeft.hours}</span>HOURS</div>
                    <div><span className="count">{timeLeft.minutes}</span>MIN</div>
                    <div><span className="count">{timeLeft.seconds}</span>SEC</div>
                </>
            );
        }

        return null;
    };

    useEffect(() => {
        // Initial countdown tick
        const shouldSetInterval = countdownTick();

        if (shouldSetInterval) {
            const timer = setInterval(() => {
                countdownTick(timer);
            }, 1000);
    
            return () => clearInterval(timer);
        }
    }, [eventStartDate]);

    return (
        <div className="swiper-slide">
            <div className="countdown-slide">
                <div className="countdown-logo"><Logo /></div>
                <div className="countdown-slide-timer">
                    <h3 className="tagline h6">{currentTagline}</h3>
                    <div className="countdown-timer">
                        {getCountdownMarkup()}
                    </div>
                </div>
            </div>
        </div>
    );
};

const getTimeLeftUntilStart = (eventStartDate: Date) => {
    const now = new Date();
    const difference = eventStartDate.getTime() - now.getTime();

    if (difference <= 0) {
        return null;
    } else {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        return { days, hours, minutes, seconds };
    }
};

export default CountdownSwiperSlide;